import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SimpleDialogService, CommonActionKey, BankAccountType, FilterParameter, QueryParamsService } from 'common';
import { readOnly, readWrite } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { BankingAccountListFilterDialogComponent } from '../banking-account-list-filter-dialog/banking-account-list-filter-dialog.component';
import { BankingAccountListFilterService } from '../banking-account-list-filter-dialog/banking-account-list-filter.service';
import {
  BankingAccount,
  BankingAccountFilters,
  BankingAccountQueryParams,
  BankingAccountQueryResult,
  BankingAccountStatus,
} from '../banking.model';
import { BankingService } from '../banking.service';

@UntilDestroy()
@Component({
    selector: 'ifb-banking-account-list',
    templateUrl: './banking-account-list.component.html',
    styleUrls: ['./banking-account-list.component.scss'],
    standalone: false
})
export class BankingAccountListComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  tableColumns: string[] = [
    'routingNumber',
    'accountNumber',
    'type',
    'name',
    'currency',
    'balance',
    'balanceDate',
    'status',
    'institutionName',
    'aggregationSuccessDate',
    'aggregationAttemptDate',
    'delete',
  ];

  filters: BankingAccountFilters;
  activeFilters: FilterParameter[] = [];
  queryParams: BankingAccountQueryParams;
  data: BankAccountListComponentData;
  canReadBankingTransactions = false;
  canWriteBankingAccount = false;
  bankingCustomerId = null;
  provider: string;
  achNumbers = {};

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly bankingAccountListFilterService: BankingAccountListFilterService,
    private readonly bankingService: BankingService,
    private readonly userPermissionService: UserPermissionService,
    private readonly queryParamsService: QueryParamsService,
    private readonly simpleDialogService: SimpleDialogService,
  ) {}

  readonly BankingAccountStatus = BankingAccountStatus;
  get onlyPendingAccounts(): boolean {
    return this.queryParams.onlyPendingAccounts;
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.dataInit.bind(this));
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe((it) => {
      this.queryParams = this.queryParamsService.init(it);
      this.activeFilters = this.bankingAccountListFilterService.addFilterParameters(this.queryParams);
    });
    this.userPermissionService
      .granted([readOnly('servicing-synced-transaction')])
      .subscribe((res) => (this.canReadBankingTransactions = res));
    this.userPermissionService
      .granted([readWrite('servicing-synced-account')])
      .subscribe((res) => (this.canWriteBankingAccount = res));

    this.filters = new BankingAccountFilters(this.queryParams);
    this.paginator.pageSize = this.queryParams.limit;
    this.paginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
  }

  activeFilterRemoved(activefilters: FilterParameter[]): void {
    if (!activefilters.length) return;
    activefilters.forEach((item) => {
      this.filters.remove(item.id);
      this.bankingAccountListFilterService.filter(this.provider, this.filters, this.bankingCustomerId);
    });
  }

  filterDialog(): void {
    BankingAccountListFilterDialogComponent.show(this.dialog, this.queryParams).subscribe((result) => {
      if (result) {
        this.filters = result;
        this.paginator.firstPage();
        this.bankingAccountListFilterService.filter(this.provider, result, this.bankingCustomerId);
      }
    });
  }

  private dataInit(data: BankAccountListComponentData): void {
    this.data = data;
    this.bankingCustomerId = this.route.snapshot.paramMap.get('bankCustomerId');
    this.provider = this.route.snapshot.paramMap.get('provider');
  }

  deleteCustomer(): void {
    this.simpleDialogService
      .openSimpleConfirmDialog('Delete customer?', 'Confirm')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.bankingService.deleteCustomer(this.provider, this.bankingCustomerId).subscribe(() => {
            this.router.navigate([`/banking/${this.provider}/customers`]);
          });
        }
      });
  }

  deleteAccount(bankingAccountId: string): void {
    this.simpleDialogService
      .openSimpleConfirmDialog('Delete account?', 'Confirm')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm)
          this.bankingService
            .deleteAccount(this.provider, this.bankingCustomerId, bankingAccountId)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.fetchData();
            });
      });
  }

  private fetchData(): void {
    this.bankingService
      .getAccounts(this.provider, this.bankingCustomerId, this.queryParams)
      .subscribe((res) => (this.data.bankingAccountQueryResult = res));
  }

  setPage(): void {
    this.bankingAccountListFilterService.filter(this.provider, this.filters, this.bankingCustomerId, this.paginator);
  }

  bankTransactionListLink(bankingAccount: BankingAccount): void {
    if (!this.canReadBankingTransactions || bankingAccount.status === BankingAccountStatus.Pending) return;
    else if (this.canReadBankingTransactions) {
      this.router.navigate([
        `/banking/${this.provider}/customers/${bankingAccount.customerId}/accounts/${bankingAccount.id}/transactions`,
      ]);
    }
  }

  achNumbersReveal(bankingAccount: BankingAccount): void {
    this.bankingService.getAchNumbers(this.provider, bankingAccount.customerId, bankingAccount.id).subscribe((ach) => {
      if (ach) this.achNumbers[bankingAccount.id] = ach;
    });
  }

  achNumbersAvailable(type: BankAccountType): boolean {
    return type === BankAccountType['Checking'] || type === BankAccountType['Savings'];
  }
}

export interface BankAccountListComponentData {
  bankingAccountQueryResult: BankingAccountQueryResult;
}
